import { useState, useEffect, useCallback } from 'react'
import { IconTrash, IconRefresh, IconBox, IconAlertTriangle } from '@tabler/icons-react'
import { gearItemsApi, type GearItem } from '../../lib/api.ts'
import { useToast } from '../../components/ui/Toast.tsx'
import { InlineError } from '../../components/ui/ErrorBoundary.tsx'

const RETENTION_DAYS = 30 // mirrors TRASH_RETENTION_DAYS on the API — move to admin config later

function daysUntilExpiry(deletedAt: string): number {
  const deleted = new Date(deletedAt).getTime()
  const expiry = deleted + RETENTION_DAYS * 24 * 60 * 60 * 1000
  return Math.max(0, Math.ceil((expiry - Date.now()) / (24 * 60 * 60 * 1000)))
}

export function RecentlyDeletedPage() {
  const { success, error } = useToast()
  const [items, setItems] = useState<GearItem[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<{ id: string; title: string } | null>(null)
  const [working, setWorking] = useState<string | null>(null) // item ID currently being acted on

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const res = await gearItemsApi.listDeleted()
      setItems(res.data ?? [])
    } catch (e: any) {
      setLoadError(e.message ?? 'Failed to load deleted items')
      error('Failed to load', e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const handleRestore = async (item: GearItem) => {
    setWorking(item.id)
    try {
      await gearItemsApi.restore(item.id)
      success('Restored', `${item.title} has been restored to your gear`)
      setItems(prev => prev.filter(i => i.id !== item.id))
    } catch (e: any) {
      error('Failed to restore', e.message)
    } finally {
      setWorking(null)
    }
  }

  const handlePermanentDelete = async () => {
    if (!confirming) return
    setWorking(confirming.id)
    try {
      await gearItemsApi.permanentDelete(confirming.id)
      success('Permanently deleted', confirming.title)
      setItems(prev => prev.filter(i => i.id !== confirming.id))
      setConfirming(null)
    } catch (e: any) {
      error('Failed to delete', e.message)
    } finally {
      setWorking(null)
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-border-default">
        <IconTrash size={15} className="text-zinc-500 shrink-0" />
        <h1 className="text-sm font-medium text-zinc-200 flex-1">
          Recently Deleted
          <span className="ml-2 text-zinc-500 font-normal text-xs">· {items.length} items</span>
        </h1>
        <span className="text-xs text-zinc-500">Items are permanently deleted after {RETENTION_DAYS} days</span>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loadError ? (
          <div className="p-6"><InlineError message={loadError} onRetry={load} /></div>
        ) : loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-sm text-zinc-500">Loading...</div>
          </div>
        ) : items.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-surface-raised border border-border-default flex items-center justify-center mx-auto mb-4">
                <IconTrash size={24} className="text-zinc-600" />
              </div>
              <h3 className="text-sm font-medium text-zinc-400 mb-1">Nothing in the trash</h3>
              <p className="text-xs text-zinc-500 max-w-xs">Deleted gear items appear here for {RETENTION_DAYS} days before being permanently removed.</p>
            </div>
          </div>
        ) : (
          <div className="p-6 max-w-3xl space-y-2">
            {items.map(item => {
              const days = item.deletedAt ? daysUntilExpiry(item.deletedAt) : 0
              const urgent = days <= 3
              const primaryPhoto = item.photos?.find(p => p.isPrimary) ?? item.photos?.[0]
              const isWorking = working === item.id

              return (
                <div key={item.id}
                  className="flex items-center gap-4 p-3 rounded-xl bg-surface-overlay border border-border-default">
                  {/* Thumbnail */}
                  <div className="w-12 h-16 rounded-lg overflow-hidden bg-surface-raised shrink-0">
                    {primaryPhoto ? (
                      <img src={primaryPhoto.url} alt={item.title} className="w-full h-full object-cover opacity-60" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <IconBox size={16} className="text-zinc-600" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-zinc-300 truncate">{item.title}</div>
                    {item.brand && <div className="text-xs text-zinc-500 mt-0.5">{item.brand}</div>}
                    <div className={`flex items-center gap-1 mt-1 text-xs ${urgent ? 'text-red-400' : 'text-zinc-500'}`}>
                      {urgent && <IconAlertTriangle size={11} />}
                      {days === 0 ? 'Expires today' : `${days} day${days !== 1 ? 's' : ''} left`}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => handleRestore(item)}
                      disabled={isWorking}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-border-default text-zinc-300 hover:text-zinc-100 hover:bg-surface-raised disabled:opacity-40 transition-colors">
                      <IconRefresh size={12} />
                      {isWorking ? 'Restoring...' : 'Restore'}
                    </button>
                    <button
                      onClick={() => setConfirming({ id: item.id, title: item.title })}
                      disabled={isWorking}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-red-800/50 text-red-400 hover:bg-red-950/40 disabled:opacity-40 transition-colors">
                      <IconTrash size={12} />
                      Delete
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Permanent delete confirmation */}
      {confirming && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-surface-overlay border border-border-default rounded-2xl shadow-2xl p-6">
            <div className="w-10 h-10 rounded-full bg-red-950 border border-red-800 flex items-center justify-center mb-4">
              <IconTrash size={18} className="text-red-400" />
            </div>
            <h3 className="text-sm font-semibold text-zinc-100 mb-2">Permanently delete?</h3>
            <p className="text-sm text-zinc-400 mb-5">
              <span className="font-medium text-zinc-200">{confirming.title}</span> will be permanently deleted and cannot be recovered.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setConfirming(null)}
                className="flex-1 px-4 py-2 rounded-lg text-sm border border-border-default text-zinc-400 hover:text-zinc-200 transition-colors">
                Cancel
              </button>
              <button onClick={handlePermanentDelete} disabled={!!working}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-red-900 text-red-200 hover:bg-red-800 disabled:opacity-50 transition-colors">
                {working ? 'Deleting...' : 'Delete Forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
